import React, {useContext, useEffect, useRef, useState} from "react";
import {
	Exist,
	Preloader,
	Table,
	TableBody,
	TableCell,
	TableDivider,
	TableHeader,
	TableHeaderCell,
	TableRow,
} from "components";
import {useTranslation} from "react-i18next";
import {ILeaguesContests, IRanking, IRankingFilters} from "modules/types";
import styled, {ThemeContext} from "styled-components";
import {ProjectName, PROJECT_NAME, RequestState} from "modules";
import {isEmpty, isNumber} from "lodash";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";
import {getCompleteActiveContests, getIsRoundComplete, getIsScoring} from "modules/selectors";
import ReactDOM from "react-dom";
import { media } from "assets/css/media";
import backgroundOfAvatar from "assets/images/backgroundOfAvatar.svg";

const ButtonLink = styled(Link)<{$disabled: boolean}>`
	cursor: ${({$disabled}) => ($disabled ? "initial" : "pointer")};
	pointer-events: ${({$disabled}) => ($disabled ? "none" : "initial")};
	text-decoration: ${({$disabled}) => ($disabled ? "none" : "underline")};
`;

const StickyHeader = styled.div<{left: number, width: number}>`
	position: fixed;
	top: 90px;

	@media screen and (min-width: ${media.tablet}) {
		top: 130px;
	}

	@media screen and (min-width: ${media.large_desktop}) {
		top: 178px;
	}
	
	left: ${({left}) => left}px;
	right: 0;
	width: ${({width}) => width}px;
	background-color: ${({theme}) => theme.global.leagues_head_background || theme.primary};
	font-family: "Qatar2022", sans-serif;
`
export const TableCellUserName = styled.td`
	font-size: 14px;
	font-weight: 500;
	line-height: 24px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 50px;
	vertical-align: middle;
	padding: 14px 14px 10px;
`;

const AvatarWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 30px;
	height: 30px;
	background: url(${backgroundOfAvatar}) no-repeat center;
	background-size: contain;
	margin-right: 10px;
`;

const Avatar = styled.img`
	width: 28px;
	height: 28px;
`;

const getFantasyDailySharedTeamUrl = (contests: ILeaguesContests[], roundId: number | string, ranking: IRanking) => {
	// Fall back to latest round if no round specified
	const round = roundId === '' ? contests.at(-1)?.id ?? '' : roundId
	return `/public-team/${ranking?.userId ?? ''}/${round}?username=${ranking.userName}`
}

interface IProps {
	rankings: IRanking[];
	user: IRanking | null;
	requestState: RequestState;
	filters: IRankingFilters;
	isGotc?: boolean;
}

interface IStickyHeaderPosition {
	width: number
	left: number
}
const defaultStickyHeaderPosition: IStickyHeaderPosition = {
	left: 0,
	width: 0,
}

export const RankingsTable: React.FC<IProps> = ({
	rankings,
	user,
	requestState,
	filters,
	isGotc = false,
}) => {
	const header = useRef<HTMLTableSectionElement>(null)
	const table = useRef<HTMLTableElement>(null)
	const [isShowStickyHeader, setIsShowStickyHeader] = useState(false);
	const [stickyHeaderPosition, setStickyHeaderPosition] = useState<IStickyHeaderPosition>(defaultStickyHeaderPosition);
	const {t, i18n} = useTranslation();
	const theme = useContext(ThemeContext);
	const isLoading = requestState === RequestState.Requested;
	const isLoaded = requestState === RequestState.Received;
	const noRankingsAvailable = isEmpty(rankings) && isLoaded;
	const isBracketPredictor = PROJECT_NAME === ProjectName.BracketsPredictor;
	const isFantasyClassic = PROJECT_NAME === ProjectName.Fantasy;
	const isFantasyDaily = PROJECT_NAME === ProjectName.FantasyDaily;
	const isScoring = useSelector(getIsScoring);
	const contests = useSelector(getCompleteActiveContests);
	const roundId = filters.startId ? filters.startId : "";
	const isRoundComplete = useSelector(getIsRoundComplete)(filters?.startId ?? 0);
	const isFantasyDailyLinkDisabled = !isScoring || !isRoundComplete

	const getRank = (ranking: IRanking) => {
		const userRank = filters.startId ? ranking.rank : ranking.overallRank;
		return userRank ? userRank : "-";
	};

	const getPoints = (ranking: IRanking) => {
		const userPoints = filters.startId ? ranking.points : ranking.overallPoints;
		return isNumber(userPoints) ? userPoints : "-";
	};

	useEffect(() => {
		document.body.onscroll = (_) => {
			const rect = header?.current?.getBoundingClientRect()
			const tableRect = table?.current?.getBoundingClientRect()
			const hasScrolledToEndOfTable = tableRect && (tableRect.height + tableRect.top - 200 < 0)
			const hasScrolledPastHeader = rect && rect.top < 120

			if ((hasScrolledPastHeader && hasScrolledToEndOfTable) || !hasScrolledPastHeader) {
				setIsShowStickyHeader(false)
				setStickyHeaderPosition(defaultStickyHeaderPosition)
			}
			else if (hasScrolledPastHeader) {
				setIsShowStickyHeader(true)
				setStickyHeaderPosition({width: rect.width, left: rect.x})
			}
		}
	}, [])

	const renderHeader = (ref?: React.Ref<HTMLTableSectionElement>) => (
		<React.Fragment>
			<colgroup>
				<col style={{width: "20%"}} />
				<col style={{width: "50%"}} />
				<col style={{width: "30%"}} />
			</colgroup>
			<TableHeader isGotc={isGotc} ref={ref}>
				<TableRow className="sticky">
					<TableHeaderCell textAlign="center">
						{t("rankings.leaderboard.rank")}
						<TableDivider className={`header ${i18n.dir()}`} />
					</TableHeaderCell>
					<TableHeaderCell>{t("rankings.leaderboard.displayname")}</TableHeaderCell>
					<TableHeaderCell textAlign="right">
						{t("rankings.leaderboard.points")}
					</TableHeaderCell>
				</TableRow>
			</TableHeader>
		</React.Fragment>
	)

	return (
		<React.Fragment>
			<Exist when={isShowStickyHeader}>
				{ReactDOM.createPortal(
					<StickyHeader {...stickyHeaderPosition}>
						<Table>
							{renderHeader()}
						</Table>
					</StickyHeader>,
					document.getElementById("root")!
				)}
			</Exist>
			<Table ref={table}>
				{renderHeader(header)}
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell textAlign="center" colSpan={3}>
								<Preloader color={theme.primary} />
							</TableCell>
						</TableRow>
					) : noRankingsAvailable ? (
						<TableRow>
							<TableCell textAlign="center" colSpan={3}>
								{t("rankings.leaderboard.empty")}
							</TableCell>
						</TableRow>
					) : (
						rankings.map((ranking, index) => {
							const isUser = isFantasyDaily
								? ranking.userId === user?.userId
								: ranking.teamId === user?.teamId;

							return (
								<TableRow key={index} highlight={isUser}>
									<TableCell textAlign="center">
										{getRank(ranking)}
										<TableDivider className={i18n.dir()} highlight={isUser} />
									</TableCell>
									<TableCellUserName>
										<AvatarWrapper>
											<Avatar src={ranking.gamezoneAvatar} alt={`${ranking.gamezoneLevel}`} />
										</AvatarWrapper>
										{isBracketPredictor ? (
											<ButtonLink
												to={`/another-brackets/${ranking?.teamId}`}
												$disabled={!isScoring}>
												{ranking.teamName}
											</ButtonLink>
										) : isFantasyClassic ? (
											<ButtonLink
												to={`/public-team/${ranking?.teamId}/${roundId}`}
												$disabled={!isScoring}>
												{ranking.teamName}
											</ButtonLink>
										) : isFantasyDaily ? (
											<ButtonLink
												to={getFantasyDailySharedTeamUrl(contests, roundId, ranking)}
												$disabled={isFantasyDailyLinkDisabled}>
												{ranking.userName}
											</ButtonLink>
										) : (
											ranking.teamName
										)}
									</TableCellUserName>
									<TableCell textAlign="right">{getPoints(ranking)}</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>
		</React.Fragment>
	);
};
